import { useState } from "react";
import { motion } from "framer-motion";
import { HERO } from "@/constants/testIds/ashwin";
import { profile, suggestions } from "@/data/resume";
import { ArrowUpRight, MapPin } from "lucide-react";

export default function Hero({ onAsk }) {
  const [q, setQ] = useState("");

  const submit = (e) => {
    e?.preventDefault?.();
    onAsk(q || "");
    setQ("");
  };

  const words = profile.role.split(" ");

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
    >
      <div className="paper-grain absolute inset-0" />

      {/* Editorial meta bar */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between text-xs font-mono-editorial uppercase text-[color:var(--muted)] mb-10 md:mb-16">
        <span>Portfolio · Vol. 01</span>
        <span className="hidden sm:inline">Mumbai, IN — 2026 Edition</span>
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Available for conversation
        </span>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-y-8 md:gap-x-8">
        {/* Left column — heading */}
        <div className="col-span-12 lg:col-span-8">
          <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] mb-6 tracking-[0.15em]">
            01 — Marketing & Strategy Professional
          </div>

          <h1 className="font-display text-[15vw] leading-[0.85] md:text-[9rem] lg:text-[11rem] font-light tracking-tighter">
            {words.map((w, i) => (
              <motion.span
                key={i}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block mr-4"
              >
                {i === 1 ? (
                  <span className="font-serif-accent italic font-light text-[color:var(--accent)]">
                    &amp; strategy
                  </span>
                ) : (
                  w
                )}
              </motion.span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 md:mt-10 max-w-2xl text-lg md:text-xl leading-relaxed text-[color:var(--charcoal)]/85"
          >
            I&rsquo;m <span className="font-medium">Ashwin Kalra</span> &mdash; a corporate strategist with a marketer&rsquo;s instinct.
            I turn <em className="font-serif-accent">data, brand and behavior</em> into growth across pharma,
            adtech and consumer goods.
          </motion.p>

          {/* Ask input */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 md:mt-14 max-w-2xl"
          >
            <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-3">
              Ask my AI — powered by my resume
            </div>
            <div className="glass-card flex items-center gap-2 pl-5 pr-2 py-2">
              <input
                data-testid={HERO.askInput}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ask me anything about my journey…"
                className="flex-1 bg-transparent border-0 outline-none text-base placeholder:text-[color:var(--muted)] py-3"
              />
              <button
                data-testid={HERO.askBtn}
                type="submit"
                className="group flex items-center gap-2 px-4 py-3 bg-[color:var(--charcoal)] text-white text-sm font-medium hover:bg-[color:var(--accent)] transition-colors"
              >
                Ask
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onAsk(s)}
                  className="text-xs px-3 py-1.5 border border-[color:var(--border)] hover:border-[color:var(--charcoal)] hover:text-[color:var(--accent)] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </motion.form>
        </div>

        {/* Right column — editorial info card */}
        <motion.aside
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="col-span-12 lg:col-span-4 lg:pl-8 lg:border-l border-[color:var(--border)] flex flex-col justify-between gap-8"
        >
          <div>
            <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em]">
              Now
            </div>
            <p className="mt-3 font-display text-2xl leading-tight">
              Deputy Manager, <br />
              <span className="font-serif-accent italic">Corporate Strategy</span>
              <br />
              @ Alkem Laboratories
            </p>
          </div>

          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>Mumbai, India</span>
            </div>
            <div>
              <div className="text-xs font-mono-editorial uppercase text-[color:var(--muted)] tracking-[0.15em] mb-1">
                Currently
              </div>
              <p className="leading-relaxed">
                Building AI-native tooling for a 12,000-strong pharma field force —
                and running INR 6.7 Cr in programmatic across 50+ brand lines.
              </p>
            </div>
            <a
              href="#contact"
              data-testid={HERO.ctaContact}
              className="editorial-link inline-flex items-center gap-2 font-medium mt-2"
            >
              Let&rsquo;s talk <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.aside>
      </div>
    </section>
  );
}
